import { INVALID_MODULE, SelectModule, SelectModuleProps } from "./base";
import { SurveyConfigModule, SurveyConfigOption, ModuleType } from "../fanApp";
import { addRequiredValidation, generateUUID } from "./util";
import {
  BuilderConfigOption,
  QuestionEditProps,
  SingleSelectBuilderConfigModule,
} from "../creator";

type SingleSelectQuestionProps = SelectModuleProps & {
  /**
   * we use this flag to keep a module from being shown to the user and from being validated on submit.
   * currently, this is only useable on single select modules since we use it to inject eventIds into
   * the survey form opaquely
   */
  hidden?: boolean;
};

export class SingleSelectQuestionModule extends SelectModule {
  protected _type: ModuleType = "SingleSelectQuestion";

  constructor({ ...rest }: SingleSelectQuestionProps) {
    super(rest);
  }

  validate(): this is Required<SingleSelectQuestionModule> {
    return true;
  }

  toBuilderConfig(): SingleSelectBuilderConfigModule {
    if (this.validate()) {
      const userValues: string[] = [];

      const builderModule: SingleSelectBuilderConfigModule = {
        actionRequired: this.actionRequired,
        id: this.id,
        label: this.label,
        options: this.options.map((opt) => {
          const option: BuilderConfigOption = {
            id: opt.id || generateUUID(),
            label: opt.label,
            hidden: !!opt.hidden,
          };
          if (opt.userDefined) {
            userValues.push(opt.id || generateUUID());
            option.userDefined = true;
          }
          return option;
        }),
        required: this.required,
        type: this.type,
      };

      if (this.hidden) {
        builderModule.hidden = true;
      }

      return builderModule;
    }

    throw new Error(INVALID_MODULE(this));
  }

  toSurveyConfig(): SurveyConfigModule {
    if (this.validate()) {
      const userValues: string[] = [];

      const configModule: SurveyConfigModule = {
        actionRequired: this.actionRequired,
        id: this.id,
        header: { children: this.label[this.languageCode] ?? "" },
        options: this.options.map((opt) => {
          const option: SurveyConfigOption = {
            id: opt.id || generateUUID(),
            label: opt.label?.[this.languageCode],
            hidden: !!opt.hidden,
          };
          if (opt.userDefined) {
            userValues.push(opt.id || generateUUID());
            option.userDefined = true;
          }
          return option;
        }),
        type: this.type,
        noBorder: this.noBorder,
        hidden: this.hidden,
      };

      if (this.required) {
        configModule.validation = addRequiredValidation();
      }

      if (this.hidden) {
        configModule.hidden = true;
      }

      return configModule;
    }

    throw new Error(INVALID_MODULE(this));
  }

  editProps(): QuestionEditProps {
    return {
      id: this.id,
      options: this.options.map((opt) => ({
        id: opt.id || generateUUID(),
        label: opt.label?.[this.languageCode],
        userDefined: opt.userDefined,
        hidden: !!opt.hidden,
      })),
      type: this.type,
      question: this.label[this.languageCode] ?? "",
      actionRequired: this.actionRequired,
      hidden: this.hidden,
    };
  }
}
